import React, { useEffect, useState } from 'react';
import { BarChart3 } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ComposedChart,
} from 'recharts';
import api from '../../services/api';
import alertService from '../../utils/alert';
import PageHeader from '../../components/PageHeader';
import FeatureLockedPage from '../../components/FeatureLockedPage';
import { PAGE, PageLoader } from '../../components/crm/ui';

const RANGE_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '60 days', value: 60 },
  { label: '90 days', value: 90 },
];

export default function SupportAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [planError, setPlanError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get('/support/analytics/summary', { params: { days } })
      .then(res => setData(res.data))
      .catch(err => {
        // Was: toast + `if (!data) return null` — a plan-gate 403 rendered
        // as a silently blank page with no indication anything was wrong.
        if (err.response?.status === 403) {
          setPlanError(err.response.data?.message || err.response.data?.error || 'This feature is not available on your current plan.');
        } else {
          alertService.error('Error', err.response?.data?.error || 'Failed to load analytics.');
        }
      })
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) {
    return <PageLoader />;
  }

  if (planError) {
    return (
      <FeatureLockedPage
        title="Support Analytics"
        subtitle="AI support widget activity overview."
        icon={BarChart3}
        featureName="Support Analytics"
        description={planError}
      />
    );
  }

  if (!data) return null;

  const { totals, conversationsByDay, ticketsByCategory, tokenUsage } = data;

  return (
    <div className={PAGE}>
      <PageHeader
        title="Support Analytics"
        subtitle="AI support widget activity overview."
        icon={BarChart3}
        actions={
          <div className="flex items-center gap-2">
            {RANGE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setDays(opt.value)}
                className={`px-3 py-1.5 rounded text-[13px] font-semibold transition-colors ${
 days === opt.value
 ? 'bg-[#0176D3] text-white'
 : 'bg-white border border-[#D8DDE6] text-[#706E6B] hover:bg-[#F3F2F2]'
 }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="sf-card p-6 border-l-4 border-[#0176D3]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase mb-1">Conversations</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{totals.conversationCount}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#FF9500]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase mb-1">Escalation Rate</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{Math.round(totals.escalationRate * 100)}%</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#2E844A]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase mb-1">Tickets Created</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{totals.ticketCount}</h3>
        </div>
      </div>

      <div className="sf-card p-6">
        <h2 className="sf-card-title mb-4">Conversations per day</h2>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={conversationsByDay}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F2F2" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="count" name="Conversations" stroke="#0176D3" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="escalated" name="Escalated" stroke="#BA0517" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="sf-card p-6">
        <h2 className="sf-card-title mb-4">Token usage vs. monthly budget</h2>
        {tokenUsage?.length === 0 ? (
          <div className="flex items-center justify-center h-[260px] text-sm text-[#706E6B]">No token data yet</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={tokenUsage}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F2F2" />
              <XAxis dataKey="period" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="totalTokens" name="Tokens used" fill="#0176D3" radius={[4, 4, 0, 0]} />
              <Bar dataKey="monthlyBudget" name="Budget" fill="#D8DDE6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="sf-card p-6">
        <h2 className="sf-card-title mb-1">Tickets by category</h2>
        <p className="text-xs text-[#706E6B] mb-4">Count and average resolution time per category</p>
        {ticketsByCategory?.length === 0 ? (
          <div className="flex items-center justify-center h-[260px] text-sm text-[#706E6B]">No ticket data yet</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={ticketsByCategory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F2F2" />
              <XAxis dataKey="category" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit="h" />
              <Tooltip formatter={(value, name) => name === 'Avg resolution (hrs)' ? `${Number(value).toFixed(1)}h` : value} />
              <Legend />
              <Bar yAxisId="left" dataKey="count" name="Tickets" fill="#FF9500" radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="avgResolutionHours" name="Avg resolution (hrs)" stroke="#2E844A" strokeWidth={2} dot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
